"use client";
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/button/Button.tsx
import { Loader2 } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

// src/utilities/styles.ts
function tw(strings, ...values) {
  return String.raw({ raw: strings }, ...values);
}

// src/button/Button.tsx
import { jsx, jsxs } from "react/jsx-runtime";
function Button(_a) {
  var _b = _a, { href } = _b, remaining = __objRest(_b, ["href"]);
  if (href) {
    return /* @__PURE__ */ jsx(Link, { href, children: /* @__PURE__ */ jsx(ButtonComponent, __spreadValues({}, remaining)) });
  }
  return /* @__PURE__ */ jsx(ButtonComponent, __spreadValues({}, remaining));
}
function ButtonComponent(_a) {
  var _b = _a, {
    variant = "inherit",
    size = "inherit",
    color = "inherit",
    leftIcon,
    rightIcon,
    className,
    children,
    loading,
    disabled,
    onClick,
    ref
  } = _b, remaining = __objRest(_b, [
    "variant",
    "size",
    "color",
    "leftIcon",
    "rightIcon",
    "className",
    "children",
    "loading",
    "disabled",
    "onClick",
    "ref"
  ]);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const isLoading = loading || asyncLoading;
  const useHoverable = variant === "inherit" || variant === "solid" || color === "inherit";
  function handleClick(event) {
    if (!onClick) return;
    const result = onClick(event);
    if (result && typeof result.then === "function") {
      setAsyncLoading(true);
      void result.then(() => {
        setAsyncLoading(false);
      });
    }
  }
  return /* @__PURE__ */ jsxs(
    "button",
    __spreadProps(__spreadValues({
      ref,
      type: "button",
      disabled: disabled || isLoading,
      className: `${useHoverable ? "not-disabled:hoverable" : ""} inline-flex items-center justify-center gap-1.5 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fg/15 disabled:cursor-not-allowed disabled:opacity-50 ${variantBaseStyles[variant]} ${sizeStyles[size]} ${variantColorStyles[variant][color]} ${className != null ? className : ""}`,
      onClick: handleClick
    }, remaining), {
      children: [
        isLoading ? /* @__PURE__ */ jsx(Loader2, { className: "animate-spin" }) : leftIcon,
        children,
        rightIcon
      ]
    })
  );
}
var variantBaseStyles = {
  inherit: "",
  solid: tw`font-medium`,
  outline: tw`font-medium`,
  ghost: ""
};
var sizeStyles = {
  inherit: "",
  sm: tw`px-2.5 py-1.5 text-xs`,
  md: tw`px-3.5 py-2 text-[13px]`,
  lg: tw`px-3.5 py-2 text-[13px]`
};
var variantColorStyles = {
  inherit: {
    inherit: "",
    primary: "",
    secondary: "",
    info: "",
    success: "",
    warn: "",
    error: ""
  },
  solid: {
    inherit: "",
    primary: tw`bg-fg text-surface hover:bg-fg/90`,
    secondary: tw`btn-status-text bg-secondary hover:bg-secondary/90`,
    info: tw`btn-status-text bg-info hover:bg-info/90`,
    success: tw`btn-status-text bg-success hover:bg-success/90`,
    warn: tw`btn-status-text bg-warn hover:bg-warn/90`,
    error: tw`btn-status-text bg-error hover:bg-error/90`
  },
  outline: {
    inherit: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    primary: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    secondary: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    info: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    success: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    warn: tw`border border-disabled bg-surface text-fg hover:bg-sub`,
    error: tw`border border-error/40 bg-surface text-error hover:bg-error/5`
  },
  ghost: {
    inherit: tw`bg-transparent text-muted hover:bg-sub hover:text-fg`,
    primary: tw`text-muted hover:bg-sub hover:text-fg`,
    secondary: tw`text-muted hover:bg-sub hover:text-fg`,
    info: tw`text-info hover:bg-info/10`,
    success: tw`text-success hover:bg-success/10`,
    warn: tw`text-warn hover:bg-warn/10`,
    error: tw`text-error hover:bg-error/10`
  }
};

// src/internal/IconButton.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var variantStyles = {
  ghost: "",
  outline: tw`border border-disabled bg-surface focus-visible:border-muted`
};
var sizeStyles2 = {
  sm: tw`rounded-lg p-1`,
  md: tw`rounded-lg p-1.5`,
  lg: tw`rounded-lg p-2`
};
function IconButton(_a) {
  var _b = _a, {
    className,
    ref,
    variant = "ghost",
    size = "md"
  } = _b, remaining = __objRest(_b, [
    "className",
    "ref",
    "variant",
    "size"
  ]);
  return /* @__PURE__ */ jsx2(
    "button",
    __spreadValues({
      ref,
      type: "button",
      className: `hoverable inline-flex items-center justify-center transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${sizeStyles2[size]} ${variantStyles[variant]} ${className != null ? className : ""}`
    }, remaining)
  );
}

export {
  __spreadValues,
  __spreadProps,
  __objRest,
  tw,
  Button,
  IconButton
};
